import {
    SovosButton,
    SovosDialog,
    SovosDialogActions,
    SovosDialogContent,
    SovosSelect,
    SovosMenuItem,
    SovosTypography,
    SovosSnackbar,
    SovosCheckbox,
    SovosBox,
    SovosChipChoice,
    SovosIconButton,
} from "mosaic-react";
import { Close, Download } from "mosaic-react-icons";
import { useEffect, useState } from "react";
import dompurify from "dompurify";
import { useGlobalValue } from "../../state/globalProvider";
import { useTheme } from "mosaic-react/styles";

const ITEM_HEIGHT = 50;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const DownloadTemplateDialog = ({ projectId }) => {
    const theme = useTheme();
    const textSecondaryColor = theme.palette?.text?.secondary;

    const { i18n, user, getDownloadTemplateData } = useGlobalValue();

    const [isOpen, setIsOpen] = useState(false);
    const [countryInput, setCountryInput] = useState();
    const [entitiesInput, setEntitiesInput] = useState([]);

    const [entities, setEntities] = useState([]);
    const [countries, setCountries] = useState([]);

    const [isProcessing, setIsProcessing] = useState(false);
    const [canDownload, setCanDownload] = useState(false);

    const [openSnackBar, setOpenSnackBar] = useState(false);
    const [snackBarVariant, setSnackBarVariant] = useState("confirmation");
    const [snackBarMessage, setSnackBarMessage] = useState("");
    const [selectedAllEntities, setSelectedAllEntities] = useState(false);

    const getEntityColumns = async (projectId, entity) => {
        const templateData = await getDownloadTemplateData(projectId, "", entity);
        const columns = templateData ? JSON.parse(templateData["columns"] || "{}") : "";
        return columns;
    };

    const createCsvTemplate = (rows, filename) => {
        const blobOptions = {
            type: "text/csv;charset=UTF-8",
        };

        const file = new Blob([rows], blobOptions);
        const a = document.createElement("a");
        a.href = dompurify.sanitize(URL.createObjectURL(file));
        a.setAttribute("download", `${filename}_template.csv`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const downloadTemplate = () => {
        setIsProcessing(true);
        let dowloadedFile = 0;
        Promise.all(
            (entitiesInput || []).map(async (entity) => {
                const headingsRow = await getEntityColumns(projectId, entity.value, user);
                if (headingsRow) {
                    createCsvTemplate(
                        headingsRow,
                        `${countryInput.taxRegistrationNumber}_${entity.value}`,
                    );
                    dowloadedFile++;
                }
            }),
        ).then(() => {
            showResultMessage(dowloadedFile, entitiesInput.length);
            setIsOpen(false);
        });
    };

    const getTemplateEntities = async (country) => {
        const templateData = await getDownloadTemplateData("", country);
        const entities = templateData ? templateData["entities"] : [];
        setEntities(entities);
    };

    const getCountries = async (projectId) => {
        const templateData = await getDownloadTemplateData(projectId);
        const countries = templateData ? templateData["countries"] : [];
        setCountries(countries);
    };

    useEffect(() => {
        if (Array.isArray(countries) && countries.length === 0 && projectId) {
            getCountries(projectId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setIsProcessing(true);
        if (countryInput) {
            getTemplateEntities(countryInput.value);
            setIsProcessing(false);
        } else {
            setEntities([]);
            setEntitiesInput([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countryInput]);

    useEffect(() => {
        setCanDownload(false);

        if (countryInput && entitiesInput?.length) {
            setCanDownload(true);
        }

        const allSelected =
            Array.isArray(entitiesInput) &&
            Array.isArray(entities) &&
            entitiesInput.length > 0 &&
            entitiesInput.length === entities.length;
        setSelectedAllEntities(allSelected);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entitiesInput, countryInput]);

    useEffect(() => {
        if (!isOpen) {
            setCountryInput(null);
            setEntitiesInput([]);
            setEntities([]);
            setIsProcessing(false);
            setCanDownload(false);
        }
    }, [isOpen]);

    const handleEntitiesChange = (event) => {
        const {
            target: { value },
        } = event;

        const lastItem = value && value[value.length - 1];

        if (lastItem?.value === "selectAll") {
            setEntitiesInput(entities || []);
            setSelectedAllEntities(true);
        } else if (lastItem?.value === "removeAll") {
            setEntitiesInput([]);
            setSelectedAllEntities(false);
        } else {
            setEntitiesInput(value);
        }
    };

    const downloadButton = () => {
        return (
            <>
                <SovosButton
                    variant="text"
                    key="downloadCSVTemplate"
                    onClick={() => {
                        setIsOpen(true);
                    }}
                >
                    {i18n`Download Template`}
                </SovosButton>
                <SovosDialog open={isOpen}>
                    <SovosBox
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            paddingBottom: "16px",
                        }}
                    >
                        <SovosBox>
                            <SovosTypography>{i18n`Download Template`}</SovosTypography>
                            <SovosTypography
                                variant="body2"
                                sx={{ color: textSecondaryColor, fontSize: "0.9rem" }}
                            >
                                {i18n`Download our template to fill in data from your ERP and upload it to SAF-T`}
                            </SovosTypography>
                        </SovosBox>
                        <SovosIconButton
                            tooltipText={i18n`Close`}
                            size={"small"}
                            onClick={() => {
                                setIsOpen(false);
                            }}
                        >
                            <Close />
                        </SovosIconButton>
                    </SovosBox>
                    <SovosDialogContent>
                        <>
                            <SovosSelect
                                onChange={(e) => setCountryInput(e.target.value)}
                                value={countryInput}
                                label={i18n`Choose country`}
                                key="country"
                            >
                                <SovosMenuItem value=""></SovosMenuItem>
                                {(countries || []).map((country) => (
                                    <SovosMenuItem value={country} key={country.value}>
                                        {country.name}
                                    </SovosMenuItem>
                                ))}
                            </SovosSelect>
                            <SovosSelect
                                onChange={handleEntitiesChange}
                                value={entitiesInput}
                                label={i18n`Choose template`}
                                multiple={true}
                                key="template"
                                autoWidth={false}
                                renderValue={(selected) => (
                                    <SovosBox
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 0.5,
                                            maxHeight: "80px",
                                            overflow: "auto",
                                        }}
                                    >
                                        {(selected || []).map((elm) =>
                                            elm.value === "selectAll" ||
                                            elm.value === "removeAll" ? (
                                                ""
                                            ) : (
                                                <SovosChipChoice key={elm.value} label={elm.name} />
                                            ),
                                        )}
                                    </SovosBox>
                                )}
                                MenuProps={MenuProps}
                            >
                                {countryInput ? (
                                    <SovosMenuItem
                                        value={
                                            selectedAllEntities
                                                ? { value: "removeAll" }
                                                : { value: "selectAll" }
                                        }
                                        divider={true}
                                    >
                                        <SovosCheckbox checked={selectedAllEntities} />
                                        {selectedAllEntities
                                            ? i18n`Remove Selected Items`
                                            : i18n`Select All Items`}
                                    </SovosMenuItem>
                                ) : (
                                    ""
                                )}

                                {(entities || []).map((entity) => (
                                    <SovosMenuItem value={entity} key={entity.value}>
                                        <SovosCheckbox
                                            checked={entitiesInput.some(
                                                (elm) => elm.value === entity.value,
                                            )}
                                        />
                                        {entity.name}
                                    </SovosMenuItem>
                                ))}
                            </SovosSelect>
                        </>
                    </SovosDialogContent>
                    <SovosDialogActions>
                        <SovosButton variant="text" onClick={() => setIsOpen(false)}>
                            {i18n`cancel`}
                        </SovosButton>
                        <SovosButton
                            startIcon={<Download />}
                            onClick={downloadTemplate}
                            disabled={isProcessing || !canDownload}
                        >
                            {i18n`Download`}
                        </SovosButton>
                    </SovosDialogActions>
                </SovosDialog>

                <SovosSnackbar
                    variant={snackBarVariant}
                    message={snackBarMessage}
                    onClose={() => setOpenSnackBar(false)}
                    open={openSnackBar}
                    timeout="medium"
                />
            </>
        );
    };

    const showResultMessage = (filesDownloaded, totalFiles) => {
        let _snackBarVariant = "confirmation";
        if (filesDownloaded === 0) {
            _snackBarVariant = "error";
        }

        if (filesDownloaded > 0 && filesDownloaded < totalFiles) {
            _snackBarVariant = "warning";
        }

        const _snackBarMessage1 = i18n`${filesDownloaded.toString()} of ${totalFiles.toString()} Files Downloaded`;

        const _snackBarMessage2 = i18n`${filesDownloaded.toString()} of ${totalFiles.toString()} File Downloaded`;

        const _snackBarMessage =
            parseInt(filesDownloaded) === 1 ? _snackBarMessage2 : _snackBarMessage1;

        setSnackBarMessage(_snackBarMessage);
        setSnackBarVariant(_snackBarVariant);
        setOpenSnackBar(true);
    };

    return countries && countries.length ? downloadButton() : "";
};

export default DownloadTemplateDialog;
